import type { BootstrapConfig, CommonsPayload, Signature, TrialResult } from './types';
import { db, type OutboxRecord } from './localDb';

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers || {})
    }
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function getBootstrap(): Promise<BootstrapConfig> {
  return jsonFetch<BootstrapConfig>('/api/bootstrap', { method: 'GET' });
}

export async function startSession(params: {
  deviceKey: string;
  consentVersion: string;
  turnstileToken?: string;
}): Promise<{ sessionId: string; deviceHashPrefix: string; turnstileOk: boolean }> {
  return jsonFetch('/api/session', {
    method: 'POST',
    body: JSON.stringify(params)
  });
}

export async function postTrials(trials: TrialResult[]): Promise<{ ok: true; accepted: number }> {
  return jsonFetch('/api/trials', {
    method: 'POST',
    body: JSON.stringify({ trials })
  });
}

export async function postSummary(sessionId: string, summary: Signature): Promise<{ ok: true }> {
  return jsonFetch('/api/trials', {
    method: 'POST',
    body: JSON.stringify({ sessionId, summary })
  });
}

export async function getCommons(): Promise<CommonsPayload> {
  return jsonFetch('/api/commons', { method: 'GET' });
}

async function flushRecord(record: OutboxRecord) {
  if (record.kind === 'trials') {
    const payload = record.payload as { trials: TrialResult[] };
    await postTrials(payload.trials);
  } else {
    const payload = record.payload as { summary: Signature };
    await postSummary(record.sessionId, payload.summary);
  }
}

export async function flushOutbox(): Promise<number> {
  const records = await db.outbox.orderBy('updatedAt').limit(25).toArray();
  let sent = 0;
  for (const record of records) {
    if (record.id == null) continue;
    try {
      await flushRecord(record);
      await db.outbox.delete(record.id);
      sent += 1;
    } catch (error) {
      await db.outbox.update(record.id, {
        attempts: record.attempts + 1,
        updatedAt: new Date().toISOString()
      });
      break;
    }
  }
  return sent;
}
